import { del, get, set } from 'idb-keyval';

import type { SyncClient } from './client';
import type { CollectionName, RemoteRecord } from './types';
import { SyncAuthError, SyncBadRequestError } from './types';

// Persistent outbound write queue. Per plan/02 §Offline behaviour:
// writes never block the kid loop. Local edits commit to IDB + the
// Zustand store synchronously; the engine enqueues a copy here, and a
// drain pass forwards them to the remote when network + auth permit.
//
// Collapse semantics — both prevent the queue from growing without
// bound during a long offline stretch:
//   * `enqueue` for an existing id REPLACES the pending entry. The
//     latest payload is the only one the server needs to see; anything
//     in between is dead state.
//   * a `delete` arriving after an `upsert` for the same id collapses
//     to just the delete.

export const OUTBOX_KEY = 'aamusankari:outbox';

export interface OutboxEntry {
  // Dedupe key, typically `${collection}:${recordId}`.
  id: string;
  collection: CollectionName;
  op: 'upsert' | 'delete';
  // null only for deletes.
  payload: (Partial<RemoteRecord> & { id: string }) | null;
  enqueuedAt: string;
  attempts: number;
}

export type DrainResult =
  | { kind: 'idle' }
  | { kind: 'drained'; sent: number }
  | { kind: 'partial'; sent: number; remaining: number; reason: 'network' }
  | { kind: 'auth-failed'; sent: number; remaining: number; error: SyncAuthError };

export interface Outbox {
  size(): number;
  list(): readonly OutboxEntry[];
  enqueue(entry: Omit<OutboxEntry, 'enqueuedAt' | 'attempts'>): Promise<void>;
  drain(client: SyncClient): Promise<DrainResult>;
  clear(): Promise<void>;
}

interface OutboxOptions {
  // Persistence callbacks default to `idb-keyval` against `OUTBOX_KEY`;
  // tests pass an in-memory pair so they don't depend on `fake-indexeddb`.
  load?: () => Promise<OutboxEntry[]>;
  save?: (entries: OutboxEntry[]) => Promise<void>;
  // Hook for tests / observability — invoked after every successful send.
  onSent?: (entry: OutboxEntry) => void;
}

const defaultLoad = async (): Promise<OutboxEntry[]> => {
  const raw = await get<OutboxEntry[]>(OUTBOX_KEY);
  return Array.isArray(raw) ? raw : [];
};

const defaultSave = async (entries: OutboxEntry[]): Promise<void> => {
  await set(OUTBOX_KEY, entries);
};

// Wipe the persisted outbox. Used by the boot legacy-tree reset
// (#12 / #15) so pre-existing UUID-shaped writes don't loop forever
// after the new bundle replaces the local tree.
export async function clearOutbox(): Promise<void> {
  await del(OUTBOX_KEY);
}

export async function createOutbox(options: OutboxOptions = {}): Promise<Outbox> {
  const load = options.load ?? defaultLoad;
  const save = options.save ?? defaultSave;
  const onSent = options.onSent;

  let entries: OutboxEntry[] = await load();

  async function persist(): Promise<void> {
    await save(entries);
  }

  // Single-flight drain. Concurrent callers join the in-flight promise;
  // the rerun flag handles entries enqueued after the loop's last
  // iteration. Without this serialisation, two `drain` calls racing on
  // shared `entries[0]` reads + `entries.slice(1)` writes silently
  // delete middle entries — see #16.
  let drainPromise: Promise<DrainResult> | null = null;
  let drainRequested = false;

  async function coreDrain(client: SyncClient): Promise<DrainResult> {
    if (entries.length === 0) return { kind: 'idle' };

    let sent = 0;
    while (entries.length > 0) {
      const head = entries[0]!;
      try {
        if (head.op === 'upsert') {
          if (!head.payload) throw new Error('outbox: upsert entry missing payload');
          await client.upsert(head.collection, head.payload);
        } else {
          await client.delete(head.collection, head.payload?.id ?? '');
        }
        // Identity-based removal — not slice(1). While we awaited the
        // network, an enqueue for the same id may have replaced our
        // head with a fresher payload (e.g. parent typed another letter
        // into the rename input). slice(1) would silently drop the
        // replacement. Removing by reference keeps it; the next loop
        // iteration picks it up and sends it.
        entries = entries.filter((e) => e !== head);
        await persist();
        sent += 1;
        console.info(`[sync:outbox] sent ${head.collection}:${head.payload?.id ?? ''}`);
        onSent?.(head);
      } catch (e) {
        if (e instanceof SyncBadRequestError) {
          // Server permanently rejected this payload (PB 400 — bad id
          // shape, missing field, validator failure). Retrying will
          // not help. Drop the entry, log, persist, and keep draining
          // the rest of the queue. See #12 / #15.
          console.warn('[sync:outbox] dropping entry rejected by server', {
            collection: head.collection,
            id: head.payload?.id ?? '',
            op: head.op,
            data: e.data,
          });
          entries = entries.filter((e) => e !== head);
          await persist();
          continue;
        }
        // Transient failure: bump the retry counter on this entry.
        // Identity-based replacement here too — if the queue was
        // mutated mid-flight, the replacement is fresher and shouldn't
        // be overwritten with a stale retry of the old payload.
        const headIdx = entries.indexOf(head);
        if (headIdx !== -1) {
          const updated: OutboxEntry = { ...head, attempts: head.attempts + 1 };
          entries = [...entries.slice(0, headIdx), updated, ...entries.slice(headIdx + 1)];
        }
        await persist();
        console.info(
          `[sync:outbox] retry ${head.collection}:${head.payload?.id ?? ''} attempt ${head.attempts + 1}`,
        );
        if (e instanceof SyncAuthError) {
          console.info(`[sync:outbox] drain auth-failed sent=${sent} remaining=${entries.length}`);
          return { kind: 'auth-failed', sent, remaining: entries.length, error: e };
        }
        // Treat anything else as a transient/network error: stop
        // draining, leave the queue, try again on the next tick.
        console.info(`[sync:outbox] drain partial sent=${sent} remaining=${entries.length}`);
        return { kind: 'partial', sent, remaining: entries.length, reason: 'network' };
      }
    }

    console.info(`[sync:outbox] drain drained sent=${sent} remaining=0`);
    return { kind: 'drained', sent };
  }

  return {
    size() {
      return entries.length;
    },
    list() {
      return entries;
    },
    async enqueue(entry) {
      const incoming: OutboxEntry = {
        ...entry,
        enqueuedAt: new Date().toISOString(),
        attempts: 0,
      };

      const existingIdx = entries.findIndex((e) => e.id === entry.id);
      if (existingIdx === -1) {
        entries = [...entries, incoming];
      } else {
        const existing = entries[existingIdx]!;
        if (entry.op === 'delete') {
          // delete-after-upsert collapses to a single delete. Keep the
          // payload `{ id }` from `incoming` so the drain can pass the
          // record id to `client.delete` — nulling it out here would
          // make the delete drop into `client.delete(c, '')` and silently
          // do nothing on the server.
          entries = [...entries.slice(0, existingIdx), incoming, ...entries.slice(existingIdx + 1)];
        } else if (existing.op === 'delete') {
          // Re-creating after a queued delete: replace the pending op
          // with an upsert so the remote sees the new payload.
          entries = [...entries.slice(0, existingIdx), incoming, ...entries.slice(existingIdx + 1)];
        } else {
          // Two upserts for the same record: keep only the latest.
          entries = [...entries.slice(0, existingIdx), incoming, ...entries.slice(existingIdx + 1)];
        }
      }
      await persist();
    },
    async drain(client) {
      if (drainPromise) {
        drainRequested = true;
        return drainPromise;
      }
      drainPromise = (async () => {
        let result: DrainResult;
        do {
          drainRequested = false;
          result = await coreDrain(client);
          if (result.kind !== 'drained' && result.kind !== 'idle') break;
        } while (drainRequested);
        return result;
      })();
      try {
        return await drainPromise;
      } finally {
        drainPromise = null;
      }
    },
    async clear() {
      entries = [];
      await persist();
    },
  };
}
